import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

// Ответы плагина kubernetes-diagnostics (plugins/kubernetes-diagnostics-backend/src/service.ts).

export type HealthStatus = 'ok' | 'warning' | 'critical';

export type HealthIssue = {
  severity: 'critical' | 'warning' | 'info';
  cluster: string;
  object: string;
  message: string;
};

export type EntityHealth = {
  entityRef: string;
  title?: string;
  status: HealthStatus;
  pods: { live: number; ready: number };
  clusters: string[];
  issues: HealthIssue[];
  alerts: { rules: number; firing: number };
  stopped: boolean;
};

export type PlatformHealth = {
  generatedAt: string;
  clusterErrors: { cluster: string; error: string }[];
  alertsError?: string;
  entities: EntityHealth[];
};

export type WorkloadSummary = {
  kind: string;
  namespace: string;
  name: string;
  desired?: number;
  ready: number;
  images: string[];
};

export type EntityWorkloads = {
  entity: string;
  clusters: { cluster: string; workloads: WorkloadSummary[] }[];
};

type State<T> = { value?: T; error?: Error; loading: boolean };

function useDiagnostics<T>(
  path: string | undefined,
  pollMs?: number,
): State<T> {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<State<T>>({ loading: Boolean(path) });

  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const base = await discoveryApi.getBaseUrl('kubernetes-diagnostics');
        const res = await fetchApi.fetch(`${base}${path}`);
        if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
        const value = (await res.json()) as T;
        if (!cancelled) setState({ value, loading: false });
      } catch (error) {
        if (!cancelled) {
          setState(s => ({
            value: s.value,
            error: error as Error,
            loading: false,
          }));
        }
      }
    };
    load();
    const timer = pollMs ? window.setInterval(load, pollMs) : undefined;
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [discoveryApi, fetchApi, path, pollMs]);

  return state;
}

export type AuditFinding = {
  severity: 'problem' | 'coverage';
  check: string;
  message: string;
};

export type CatalogAudit = {
  generatedAt: string;
  checkedEntities: number;
  summary: { problems: number; coverage: number; entitiesWithFindings: number };
  errors: string[];
  entities: { entityRef: string; title?: string; findings: AuditFinding[] }[];
};

/** Проверка актуальности каталога (ночной результат из кэша плагина). */
export const useCatalogAudit = () =>
  useDiagnostics<CatalogAudit>('/catalog-audit');

/** Здоровье всех сервисов в k8s, обновляется раз в минуту (на сервере тоже кэш на минуту). */
export const usePlatformHealth = () =>
  useDiagnostics<PlatformHealth>('/health', 60_000);

export const useEntityWorkloads = (entityRef: string) =>
  useDiagnostics<EntityWorkloads>(
    `/workloads?entityRef=${encodeURIComponent(entityRef)}`,
  );

export type DeliveryRun = {
  workflow: string;
  conclusion: string | null;
  sha: string;
  startedAt: string;
  finishedAt: string;
  url: string;
};

export type Delivery = {
  repository: string;
  defaultBranch: string;
  prodWorkflows: string[];
  metrics: {
    windowDays: number;
    deploys: number;
    deploysPerWeek: number;
    failedProdBuilds: number;
    failedProdBuildsPct: number;
    medianLeadTimeHours?: number;
    lastDeployAt?: string;
  };
  recentDeploys: DeliveryRun[];
  recentFailures: DeliveryRun[];
  releases: { name: string; tag: string; publishedAt: string; url: string }[];
};

/** Релизы и метрики доставки по репозиторию сущности (кэш на сервере 30 минут). */
export const useEntityDelivery = (entityRef: string) =>
  useDiagnostics<Delivery>(
    `/delivery?entityRef=${encodeURIComponent(entityRef)}`,
  );

export type QueueIssue = {
  severity: 'critical' | 'warning';
  queue?: string;
  message: string;
};

export type RabbitQueue = {
  queue: string;
  ready: number;
  unacked: number;
  consumers: number;
  publishedPerSec: number;
  readyDelta15m: number;
};

export type QueueStatus =
  | { type: 'none' }
  | {
      type: 'rabbitmq';
      totals: {
        queues: number;
        ready: number;
        unacked: number;
        consumers: number;
        publishedPerSec: number;
      };
      alarms: string[];
      issues: QueueIssue[];
      topQueues: RabbitQueue[];
    }
  | {
      type: 'bull';
      issues: QueueIssue[];
      queues: {
        queue: string;
        counts: Record<string, number>;
        failedDelta1h: number;
      }[];
    };

/** Очереди RabbitMQ/BullMQ сущности по метрикам, обновляется раз в минуту. */
export const useEntityQueues = (entityRef: string) =>
  useDiagnostics<QueueStatus>(
    `/queues?entityRef=${encodeURIComponent(entityRef)}`,
    60_000,
  );

export type WorkflowRun = {
  workflow: string;
  title: string;
  branch: string;
  event: string;
  status: string;
  conclusion: string | null;
  sha: string;
  createdAt: string;
  url: string;
};

export const useEntityWorkflowRuns = (entityRef: string) =>
  useDiagnostics<{ repository: string; runs: WorkflowRun[] }>(
    `/workflow-runs?entityRef=${encodeURIComponent(entityRef)}`,
  );

export type Severity = 'critical' | 'high' | 'medium' | 'low';

export type DependencyAlert = {
  number: number;
  severity: Severity;
  package: string;
  ecosystem: string;
  manifest: string;
  summary: string;
  vulnerableRange: string;
  patchedVersion?: string;
  ghsa: string;
  createdAt: string;
  url: string;
};

export type DependencyAlerts = {
  repository: string;
  available: boolean;
  reason?: string;
  counts: Record<Severity, number>;
  alerts: DependencyAlert[];
};

export const useEntityDependencyAlerts = (entityRef: string) =>
  useDiagnostics<DependencyAlerts>(
    `/dependabot?entityRef=${encodeURIComponent(entityRef)}`,
  );

export type QueueOverviewItem = {
  entity: string;
  type: 'rabbitmq' | 'bull';
  issues: QueueIssue[];
  error?: string;
  totals?: Record<string, number>;
};

export const useQueuesOverview = () =>
  useDiagnostics<QueueOverviewItem[]>('/queues/overview', 60_000);

export type TaskQueueStatus = {
  name: string;
  entity: string;
  pollers: { workflow: number; activity: number; lastAccessAt?: string };
  backlog: number;
  backlogAgeSeconds: number;
  running: number;
  failed24h: number;
  recentFailures: {
    workflowType: string;
    workflowId: string;
    status: string;
    closedAt?: string;
    url?: string;
  }[];
};

export const useEntityTemporal = (entityRef: string) =>
  useDiagnostics<{ queues: TaskQueueStatus[]; issues?: HealthIssue[] }>(
    `/temporal?entityRef=${encodeURIComponent(entityRef)}`,
    60_000,
  );

export type FlagSummary = {
  key: string;
  description: string;
  tags: string[];
  valueType: string;
  updatedAt: string;
  version?: number;
  publishedBy?: string;
  environments: Record<
    string,
    {
      enabled: boolean;
      defaultValue: string;
      rules: number;
      activeRules: number;
    }
  >;
  url?: string;
};

export type GrowthBookOverview =
  | { configured: false }
  | {
      configured: true;
      total: number;
      changed: FlagSummary[];
      flags: FlagSummary[];
    };

export const useEntityGrowthBook = (entityRef: string) =>
  useDiagnostics<GrowthBookOverview>(
    `/growthbook?entityRef=${encodeURIComponent(entityRef)}`,
  );

export type TimelineEvent = {
  time: string;
  end?: string;
  source: string;
  entity?: string;
  platform?: boolean;
  severity: 'info' | 'warning' | 'critical';
  summary: string;
  details?: string[];
  url?: string;
};

export type Timeline = {
  from: string;
  to: string;
  stepMinutes: number;
  truncated: boolean;
  sources: {
    source: string;
    status: 'ok' | 'error' | 'disabled';
    count: number;
    error?: string;
  }[];
  events: TimelineEvent[];
};

export const useEntityTimeline = (
  entityRef: string,
  hours: number,
  dependencies: boolean,
) =>
  useDiagnostics<Timeline>(
    `/timeline?entityRef=${encodeURIComponent(
      entityRef,
    )}&hours=${hours}&dependencies=${dependencies}`,
    120_000,
  );

export const STATUS_COLORS: Record<HealthStatus, string> = {
  critical: '#E5383B',
  warning: '#F2A900',
  ok: '#06AA2A',
};

export const STATUS_LABELS: Record<HealthStatus, string> = {
  critical: 'авария',
  warning: 'внимание',
  ok: 'в порядке',
};

/** Состояние для отображения: исправный сервис, у которого все реплики в 0, показываем остановленным. */
export type HealthState = HealthStatus | 'stopped';

export const healthState = (e: EntityHealth): HealthState =>
  e.status === 'ok' && e.stopped ? 'stopped' : e.status;

export const STATE_COLORS: Record<HealthState, string> = {
  ...STATUS_COLORS,
  stopped: '#8A8FA8',
};

export const STATE_LABELS: Record<HealthState, string> = {
  ...STATUS_LABELS,
  stopped: 'остановлен',
};

export const SEVERITY_MARKS: Record<HealthIssue['severity'], string> = {
  critical: '✕',
  warning: '▲',
  info: '•',
};

export const SEVERITY_COLORS: Record<HealthIssue['severity'], string> = {
  critical: STATUS_COLORS.critical,
  warning: STATUS_COLORS.warning,
  info: '#8A8FA8',
};

const OBJECT_KINDS: Record<string, string> = {
  Deployment: 'Деплоймент',
  StatefulSet: 'StatefulSet',
  Pod: 'Под',
  HPA: 'Автомасштабирование',
  Queue: 'Очередь',
  TaskQueue: 'Task queue',
};

/**
 * Человекочитаемая причина: «Алерт «X» — сработал», «Рестарты подов…», «Деплоймент X: …».
 * Для незнакомых объектов — как есть («<объект>: <текст>»), без текста — «проблема без описания».
 */
export function issueText(issue: HealthIssue) {
  const message = issue.message?.trim() || 'проблема без описания';
  const object = issue.object?.trim() ?? '';
  if (object.startsWith('Alert/')) {
    return `Алерт «${object.slice('Alert/'.length)}» — ${message}`;
  }
  if (object === 'Broker') return `Брокер: ${message}`;
  if (!object || object === 'Pods') {
    return message.charAt(0).toUpperCase() + message.slice(1);
  }
  const slash = object.indexOf('/');
  if (slash < 0) return `${object}: ${message}`;
  const kind = object.slice(0, slash);
  return `${OBJECT_KINDS[kind] ?? kind} ${object.slice(slash + 1)}: ${message}`;
}

/** Текст на случай, когда статус проблемный, а конкретных причин в ответе нет. */
export const UNKNOWN_CAUSE =
  'Причина не определена — откройте вкладку Kubernetes или страницу сервиса';

/** Где обнаружено: кластер или Grafana. */
export const issueSource = (issue: HealthIssue) =>
  issue.cluster === 'grafana' ? 'Grafana' : issue.cluster;

/** Число проблем без замечаний (info). */
export const problemCount = (e: EntityHealth) =>
  e.issues.filter(i => i.severity !== 'info').length;

/** Страница сущности: вкладка Kubernetes, если сущность есть в кластерах, иначе обзор. */
export function healthLinkPath(entity: EntityHealth) {
  if (entity.clusters.length) return kubernetesTabPath(entity.entityRef);
  const [kind, rest] = entity.entityRef.split(':');
  const [namespace, name] = rest.split('/');
  return `/catalog/${namespace}/${kind}/${name}`;
}

/** `component:default/signaling-api` → путь страницы сущности. */
export function entityPagePath(entityRef: string) {
  const [kind, rest] = entityRef.split(':');
  const [namespace, name] = rest.split('/');
  return `/catalog/${namespace}/${kind}/${name}`;
}

/** `component:default/signaling-api` → путь вкладки Kubernetes сущности. */
export function kubernetesTabPath(entityRef: string) {
  const [kind, rest] = entityRef.split(':');
  const [namespace, name] = rest.split('/');
  return `/catalog/${namespace}/${kind}/${name}/kubernetes`;
}
